import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Save,
  Trash2,
  UserCircle,
  Loader2,
  Plus,
  MapPin,
  Briefcase,
  GraduationCap,
  Monitor,
  Leaf,
  KeyRound,
  Eye,
  EyeOff,
} from 'lucide-react';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import {
  deleteUser,
  signOut,
  linkWithCredential,
  EmailAuthProvider,
  updatePassword,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
} from 'firebase/auth';
import LocationAutocomplete from '../components/LocationAutocomplete';
import TagInput from '../components/TagInput';
import SelectDropdown from '../components/SelectDropdown';

const API = 'https://aglomera.com/api';

const TYPES_CONTRAT_PROFIL = [
  'CDI', 'CDD', 'Stage', 'Alternance',
  'VIE / Graduate', 'Freelance', 'Fonctionnaire', 'Intérimaire/Saisonnier',
] as const;

const STATUT_OPTIONS = [
  { value: '', label: '— Non renseigné —' },
  { value: 'etudiant', label: 'Étudiant' },
  { value: 'fin_etudes', label: 'Étudiant en fin d\'études' },
  { value: 'en_cdi', label: 'Déjà en CDI' },
  { value: 'recherche_emploi', label: 'En recherche d\'emploi' },
];

const TRANCHE_AGE_OPTIONS = [
  { value: '', label: '— Non renseigné —' },
  { value: 'moins_22', label: 'Moins de 22 ans' },
  { value: '22_27', label: '22 – 27 ans' },
  { value: '28_35', label: '28 – 35 ans' },
  { value: '36_45', label: '36 – 45 ans' },
  { value: '46_plus', label: '46 ans et +' },
];

const TELETRAVAIL_OPTIONS = [
  { value: '', label: '— Non renseigné —' },
  { value: 'important', label: 'Télétravail important' },
  { value: 'partiel', label: 'Télétravail partiel' },
  { value: 'aucun', label: 'Pas de télétravail (présentiel)' },
];

const ESG_OPTIONS = [
  { value: '', label: '— Non renseigné —' },
  { value: 'indifferent', label: 'Indifférent' },
  { value: 'faible', label: 'Peu important' },
  { value: 'modere', label: 'Modéré' },
  { value: 'important', label: 'Important' },
];

/** Fallback statique si l'API Onisep est indisponible */
const FORMATION_FALLBACK = [
  'CAP / BEP', 'Bac général', 'Bac technologique', 'Bac professionnel',
  'BTS (Bac +2)', 'BUT (Bac +3)', 'DUT (Bac +2)',
  'Licence (Bac +3)', 'Licence professionnelle', 'Bachelor (Bac +3)',
  'Master 1 (Bac +4)', 'Master 2 (Bac +5)', 'Master of Science (MSc)',
  'MBA', 'DESS', 'Programme Grande École (PGE)',
  'École de commerce (Bac +5)', 'École d\'ingénieur (Bac +5)',
  'Doctorat / PhD', 'DBA', 'DSCG', 'DCG',
  'CPGE (Classes préparatoires)',
];

/* ── Composant autocomplete Formation (API Onisep + fallback statique) ── */
const FormationAutocomplete: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const fetchOnisep = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSuggestions(FORMATION_FALLBACK.filter(f => f.toLowerCase().includes(q.toLowerCase())).slice(0, 8));
      return;
    }
    setFetching(true);
    try {
      const url = `https://api.opendata.onisep.fr/api/1.0/dataset/5fa591127f501/search?q=${encodeURIComponent(q)}&size=8`;
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const results: string[] = (data.results || [])
        .map((r: Record<string, string>) => r.libelle_formation_principal || r.libelle || '')
        .filter(Boolean);
      setSuggestions(results.length > 0 ? results : FORMATION_FALLBACK.filter(f => f.toLowerCase().includes(q.toLowerCase())).slice(0, 8));
    } catch {
      setSuggestions(FORMATION_FALLBACK.filter(f => f.toLowerCase().includes(q.toLowerCase())).slice(0, 8));
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOnisep(value), 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, open, fetchOnisep]);

  // Fermer si clic en dehors
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); fetchOnisep(value); }}
          placeholder="Ex : Master 2 Finance, BTS Comptabilité…"
          className={inputCls}
          style={{ borderColor: '#c7d7f5' }}

          onFocusCapture={e => (e.currentTarget.style.borderColor = '#2a52a8')}
          onBlurCapture={e => (e.currentTarget.style.borderColor = '#c7d7f5')}
        />
        {fetching && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-300" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1.5 bg-white rounded-xl border border-gray-100 shadow-xl overflow-hidden max-h-60 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s}
              type="button"
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#f0f4ff] hover:text-[#2a52a8] transition-colors border-b border-gray-50 last:border-0"
              onMouseDown={e => { e.preventDefault(); onChange(s); setOpen(false); }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Slider personnalisé ── */
const RangeSlider: React.FC<{
  min: number; max: number; step: number;
  value: number; onChange: (v: number) => void;
}> = ({ min, max, step, value, onChange }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative pt-1 pb-1">
      <style>{`
        .rng-custom { -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 9999px; outline: none; cursor: pointer; }
        .rng-custom::-webkit-slider-thumb { -webkit-appearance: none; width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; box-shadow: 0 2px 10px rgba(42,82,168,0.22); cursor: pointer; transition: transform .15s, box-shadow .15s; }
        .rng-custom::-webkit-slider-thumb:hover { transform: scale(1.18); box-shadow: 0 3px 16px rgba(42,82,168,0.35); }
        .rng-custom::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 2.5px solid #2a52a8; box-shadow: 0 2px 10px rgba(42,82,168,0.22); cursor: pointer; }
        .rng-custom:focus::-webkit-slider-thumb { box-shadow: 0 0 0 3px rgba(42,82,168,0.18), 0 2px 10px rgba(42,82,168,0.22); }
      `}</style>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        className="rng-custom"
        style={{ background: `linear-gradient(to right, #2a52a8 ${pct}%, #e5e7eb ${pct}%)` }}
      />
    </div>
  );
};

export interface LocalisationPref {
  label: string;
  distance_km: number;
}

const inputCls =
  'w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors bg-white text-gray-800';

const MonProfil: React.FC = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── Détection du provider (Google uniquement vs email+password) ──
  const [hasPasswordProvider, setHasPasswordProvider] = useState(
    () => user?.providerData?.some(p => p.providerId === 'password') ?? false,
  );

  // ── Section mot de passe ──
  const [pwMode, setPwMode] = useState<'idle' | 'set' | 'change' | 'forgot'>('idle');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const [emailAlertes, setEmailAlertes] = useState('');
  const [statutCandidat, setStatutCandidat] = useState('');
  const [trancheAge, setTrancheAge] = useState('');
  const [formation, setFormation] = useState('');
  const [typesContrat, setTypesContrat] = useState<string[]>([]);
  const [postesVises, setPostesVises] = useState<string[]>([]);
  const [anneesExperience, setAnneesExperience] = useState<string>('');
  const [localisations, setLocalisations] = useState<LocalisationPref[]>([{ label: '', distance_km: 40 }]);
  const [teletravail, setTeletravail] = useState('');
  const [importanceEsg, setImportanceEsg] = useState('');
  const [cvNomFichier, setCvNomFichier] = useState('');

  const loadProfil = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/profil/`, { headers: await apiHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setEmailAlertes(d.email_alertes || u.email || '');
      setStatutCandidat(d.statut_candidat || '');
      setTrancheAge(d.tranche_age || '');
      setFormation(d.formation || '');
      setTypesContrat(Array.isArray(d.types_contrat_recherches) ? d.types_contrat_recherches : []);
      setPostesVises(Array.isArray(d.postes_vises) ? d.postes_vises : []);
      setAnneesExperience(d.annees_experience != null ? String(d.annees_experience) : '');
      const locs = Array.isArray(d.localisations) && d.localisations.length > 0 ? d.localisations : [{ label: '', distance_km: 40 }];
      setLocalisations(
        locs.map((x: { label?: string; distance_km?: number }) => ({
          label: x.label || '',
          distance_km: typeof x.distance_km === 'number' ? x.distance_km : 40,
        })),
      );
      setTeletravail(d.teletravail || '');
      setImportanceEsg(d.importance_esg || '');
      setCvNomFichier(d.cv_nom_fichier || '');
    } catch {
      setError('Impossible de charger le profil.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfil();
  }, [loadProfil]);

  const toggleTypeContrat = (t: string) => {
    setTypesContrat(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));
  };

  const updateLoc = (index: number, patch: Partial<LocalisationPref>) => {
    setLocalisations(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addLocalisation = () => setLocalisations(prev => [...prev, { label: '', distance_km: 40 }]);
  const removeLocalisation = (index: number) => {
    setLocalisations(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSave = async () => {
    const u = auth.currentUser;
    if (!u) return;
    setSaving(true);
    setError('');
    setSavedFlash(false);
    try {
      const locPayload = localisations
        .filter(row => row.label.trim())
        .map(row => ({ label: row.label.trim(), distance_km: Math.min(200, Math.max(5, row.distance_km)) }));

      const body: Record<string, unknown> = {
        email_alertes: emailAlertes.trim() || null,
        statut_candidat: statutCandidat || null,
        tranche_age: trancheAge || null,
        formation: formation.trim(),
        types_contrat_recherches: typesContrat,
        postes_vises: postesVises,
        annees_experience:
          anneesExperience === '' ? null : Math.min(60, Math.max(0, parseInt(anneesExperience, 10) || 0)),
        localisations: locPayload,
        teletravail: teletravail || null,
        importance_esg: importanceEsg || null,
        cv_nom_fichier: cvNomFichier || '',
      };

      const res = await fetch(`${API}/profil/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch {
      setError('Enregistrement impossible, réessayez.');
    } finally {
      setSaving(false);
    }
  };

  const resetPwForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPwError('');
    setForgotEmail('');
    setResetSent(false);
  };

  const openPwMode = (mode: 'set' | 'change' | 'forgot') => {
    resetPwForm();
    setPwSuccess('');
    setPwMode(mode);
    if (mode === 'forgot') {
      setForgotEmail(user?.email ?? '');
    }
  };

  const handleSetPassword = async () => {
    if (newPassword !== confirmNewPassword) { setPwError('Les mots de passe ne correspondent pas.'); return; }
    if (newPassword.length < 6) { setPwError('Mot de passe trop court (6 caractères minimum).'); return; }
    const u = auth.currentUser;
    if (!u || !u.email) return;
    setPwLoading(true);
    setPwError('');
    try {
      const credential = EmailAuthProvider.credential(u.email, newPassword);
      await linkWithCredential(u, credential);
      await u.reload();
      setHasPasswordProvider(true);
      setPwSuccess('Mot de passe défini avec succès !');
      setPwMode('idle');
      resetPwForm();
    } catch (e: any) {
      const code: string = e?.code ?? '';
      if (code === 'auth/provider-already-linked') {
        setPwError('Un mot de passe est déjà associé à ce compte. Utilisez "Modifier".');
      } else {
        setPwError('Erreur lors de la définition du mot de passe. Réessayez.');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) { setPwError('Les mots de passe ne correspondent pas.'); return; }
    if (newPassword.length < 6) { setPwError('Nouveau mot de passe trop court (6 caractères minimum).'); return; }
    const u = auth.currentUser;
    if (!u || !u.email) return;
    setPwLoading(true);
    setPwError('');
    try {
      const credential = EmailAuthProvider.credential(u.email, currentPassword);
      await reauthenticateWithCredential(u, credential);
      await updatePassword(u, newPassword);
      setPwSuccess('Mot de passe modifié avec succès !');
      setPwMode('idle');
      resetPwForm();
    } catch (e: any) {
      const code: string = e?.code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setPwError('Mot de passe actuel incorrect.');
      } else if (code === 'auth/requires-recent-login') {
        setPwError('Session expirée. Déconnectez-vous et reconnectez-vous avant de modifier votre mot de passe.');
      } else {
        setPwError('Erreur lors de la modification. Réessayez.');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const emailToReset = forgotEmail.trim();
    if (!emailToReset) { setPwError('Veuillez saisir votre adresse email.'); return; }
    setPwLoading(true);
    setPwError('');
    try {
      await sendPasswordResetEmail(auth, emailToReset);
      setResetSent(true);
    } catch (e: any) {
      const code: string = e?.code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        // Pour la sécurité, on affiche quand même un succès
        setResetSent(true);
      } else {
        setPwError('Impossible d\'envoyer l\'email. Réessayez.');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const u = auth.currentUser;
    if (!u) return;
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`${API}/compte/`, {
        method: 'DELETE',
        headers: await apiHeaders(),
      });
      if (!res.ok && res.status !== 204) throw new Error('server');
      try {
        await deleteUser(u);
      } catch {
        /* Firebase peut exiger une reconnexion récente pour deleteUser */
      }
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch {
      setError('Impossible de finaliser la suppression. Réessayez ou contactez le support.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 w-full flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2a52a8]" />
      </div>
    );
  }

  return (
    <div className="p-8 w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Mon profil</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez vos informations et préférences</p>
      </div>

      {/* ── Détails de mon compte ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-5">Détails de mon compte</h2>

        {/* E-mail d'inscription — figé */}
        <div className="mb-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-1 flex items-center gap-2">
            <Mail size={15} style={{ color: '#2a52a8' }} />
            E-mail d&apos;inscription
          </h3>
          <p className="text-xs text-gray-400 mb-2">Adresse avec laquelle vous vous êtes inscrit(e) — non modifiable</p>
          <div
            className="w-full px-4 py-2.5 rounded-xl border text-sm text-gray-500 bg-gray-50 select-all"
            style={{ borderColor: '#e5e7eb' }}
          >
            {user?.email ?? '—'}
          </div>
        </div>

        {/* E-mail de réception des alertes — modifiable */}
        <div className="mb-6">
          <h3 className="font-semibold text-sm text-gray-700 mb-1 flex items-center gap-2">
            <Mail size={15} style={{ color: '#2a52a8' }} />
            E-mail de réception des alertes
          </h3>
          <p className="text-xs text-gray-400 mb-2">
            Les alertes jobs seront envoyées ici. Vous pouvez indiquer une adresse différente de votre adresse d&apos;inscription.
          </p>
          <input
            type="email"
            value={emailAlertes}
            onChange={e => setEmailAlertes(e.target.value)}
            className={inputCls}
            style={{ borderColor: '#c7d7f5' }}
            onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
            onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
          />
        </div>

        {/* ── Section mot de passe ─────────────────────────────────────────── */}
        <div className="border-t border-gray-100 pt-5 mb-5">
          <h3 className="font-semibold text-sm text-gray-700 mb-1 flex items-center gap-2">
            <KeyRound size={15} style={{ color: '#2a52a8' }} />
            Mot de passe
          </h3>

          {pwSuccess && (
            <p className="mt-2 text-sm text-green-700 bg-green-50 px-4 py-2.5 rounded-xl">{pwSuccess}</p>
          )}

          {/* ── Utilisateur Google uniquement (pas de mot de passe) ── */}
          {!hasPasswordProvider && pwMode === 'idle' && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-3">
                Vous vous êtes inscrit(e) via Google. Vous pouvez définir un mot de passe pour vous connecter directement avec votre e-mail.
              </p>
              <button
                type="button"
                onClick={() => openPwMode('set')}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#c7d7f5] text-[#2a52a8] hover:bg-[#f0f4ff] transition-colors"
              >
                <KeyRound size={15} />
                Définir un mot de passe
              </button>
            </div>
          )}

          {!hasPasswordProvider && pwMode === 'set' && (
            <div className="mt-3 space-y-3">
              {/* Nouveau mot de passe */}
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  placeholder="Nouveau mot de passe"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: '#c7d7f5', paddingRight: '2.5rem' }}
                  onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                  onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                />
                <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Confirmer */}
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirmer le mot de passe"
                  value={confirmNewPassword}
                  onChange={e => setConfirmNewPassword(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: '#c7d7f5', paddingRight: '2.5rem' }}
                  onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                  onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                />
                <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {pwError && <p className="text-xs text-red-600">{pwError}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSetPassword}
                  disabled={pwLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition-opacity"
                  style={{ background: '#2a52a8' }}
                >
                  {pwLoading ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={() => { setPwMode('idle'); resetPwForm(); }}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* ── Utilisateur avec mot de passe existant ── */}
          {hasPasswordProvider && pwMode === 'idle' && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-3">
                Votre compte est protégé par un mot de passe.
              </p>
              <button
                type="button"
                onClick={() => openPwMode('change')}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#c7d7f5] text-[#2a52a8] hover:bg-[#f0f4ff] transition-colors"
              >
                <KeyRound size={15} />
                Modifier le mot de passe
              </button>
            </div>
          )}

          {hasPasswordProvider && pwMode === 'change' && (
            <div className="mt-3 space-y-3">
              {/* Mot de passe actuel */}
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  placeholder="Mot de passe actuel"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: '#c7d7f5', paddingRight: '2.5rem' }}
                  onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                  onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                />
                <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Nouveau mot de passe */}
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  placeholder="Nouveau mot de passe"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: '#c7d7f5', paddingRight: '2.5rem' }}
                  onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                  onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                />
                <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* Confirmer */}
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirmer le nouveau mot de passe"
                  value={confirmNewPassword}
                  onChange={e => setConfirmNewPassword(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: '#c7d7f5', paddingRight: '2.5rem' }}
                  onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                  onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                />
                <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {pwError && <p className="text-xs text-red-600">{pwError}</p>}
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleChangePassword}
                  disabled={pwLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition-opacity"
                  style={{ background: '#2a52a8' }}
                >
                  {pwLoading ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={() => { setPwMode('idle'); resetPwForm(); }}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
              {/* Mot de passe oublié */}
              <button
                type="button"
                onClick={() => openPwMode('forgot')}
                className="text-xs text-[#2a52a8] hover:underline mt-1"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}

          {/* ── Formulaire mot de passe oublié (commun aux deux cas) ── */}
          {pwMode === 'forgot' && (
            <div className="mt-3 space-y-3">
              {resetSent ? (
                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                  <p className="text-sm text-green-700 font-medium">Email envoyé !</p>
                  <p className="text-xs text-green-600 mt-1">
                    Un lien de réinitialisation a été envoyé à <strong>{forgotEmail}</strong>. Vérifiez aussi vos spams.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-400">
                    Nous vous enverrons un lien pour réinitialiser votre mot de passe.
                  </p>
                  <input
                    type="email"
                    placeholder="Votre adresse email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    className={inputCls}
                    style={{ borderColor: '#c7d7f5' }}
                    onFocus={e => { e.target.style.borderColor = '#2a52a8'; }}
                    onBlur={e => { e.target.style.borderColor = '#c7d7f5'; }}
                  />
                  {pwError && <p className="text-xs text-red-600">{pwError}</p>}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={pwLoading}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition-opacity"
                      style={{ background: '#2a52a8' }}
                    >
                      {pwLoading ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                      Envoyer le lien
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPwMode(hasPasswordProvider ? 'change' : 'idle'); resetPwForm(); }}
                      className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </>
              )}
              {resetSent && (
                <button
                  type="button"
                  onClick={() => { setPwMode('idle'); resetPwForm(); }}
                  className="text-xs text-gray-400 hover:underline"
                >
                  Fermer
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Suppression du compte ─────────────────────────────────────────── */}
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-3">
            Supprime définitivement vos données sur nos serveurs (alertes, favoris, profil). Vous devrez aussi valider la
            suppression du compte chez le fournisseur de connexion si demandé.
          </p>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Supprimer mon compte
          </button>
        </div>
      </div>

      {/* ── Mon profil candidat ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
          <UserCircle size={20} style={{ color: '#2a52a8' }} />
          Mon profil candidat
        </h2>
        <p className="text-xs text-gray-400 mb-6">
          Ces informations serviront au futur filtrage intelligent des offres (ex. similarité avec votre parcours).
        </p>

        {/* Situation */}
        <div className="space-y-5 mb-8">
          <div>
            <label id="label-statut" htmlFor="field-statut" className="block text-sm font-semibold text-gray-800 mb-1">
              Statut
            </label>
            <SelectDropdown
              id="field-statut"
              aria-labelledby="label-statut"
              value={statutCandidat}
              onChange={setStatutCandidat}
              options={STATUT_OPTIONS}
            />
          </div>

          <div>
            <label id="label-tranche-age" htmlFor="field-tranche-age" className="block text-sm font-semibold text-gray-800 mb-1">
              Tranche d&apos;âge
            </label>
            <SelectDropdown
              id="field-tranche-age"
              aria-labelledby="label-tranche-age"
              value={trancheAge}
              onChange={setTrancheAge}
              options={TRANCHE_AGE_OPTIONS}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <GraduationCap size={15} className="text-[#2a52a8]" />
              Formation / diplôme
            </label>
            <p className="text-xs text-gray-400 mb-2">Recherche parmi les formations Onisep — saisie libre possible</p>
            <FormationAutocomplete value={formation} onChange={setFormation} />
          </div>
        </div>

        {/* Projet pro */}
        <div className="border-t border-gray-100 pt-6 space-y-5 mb-8">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Projet professionnel</h3>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Briefcase size={15} className="text-[#2a52a8]" />
              Types de contrat recherchés
            </label>
            <div className="flex flex-wrap gap-2">
              {TYPES_CONTRAT_PROFIL.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTypeContrat(t)}
                  className="px-4 py-2 rounded-xl text-sm font-medium border transition-all"
                  style={
                    typesContrat.includes(t)
                      ? { background: '#2a52a8', color: 'white', borderColor: '#2a52a8' }
                      : { background: 'white', color: '#374151', borderColor: '#e5e7eb' }
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Postes visés</label>
            <p className="text-xs text-gray-400 mb-2">Appuyez sur Entrée pour valider chaque intitulé</p>
            <TagInput
              values={postesVises}
              onChange={setPostesVises}
              placeholder="Ex : Analyste M&A, Associate PE..."
              tagBg="#e6edfa"
              tagColor="#2a52a8"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Années d&apos;expérience dans le domaine visé
            </label>
            <input
              type="number"
              min={0}
              max={60}
              value={anneesExperience}
              onChange={e => setAnneesExperience(e.target.value)}
              placeholder="0"
              className={`${inputCls} max-w-[120px]`}
              style={{ borderColor: '#c7d7f5' }}
              onFocus={e => {
                e.target.style.borderColor = '#2a52a8';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#c7d7f5';
              }}
            />
          </div>
        </div>

        {/* Lieu */}
        <div className="border-t border-gray-100 pt-6 space-y-4 mb-8">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            <MapPin size={15} className="text-[#2a52a8]" />
            Localisation de recherche
          </h3>
          <p className="text-xs text-gray-400">
            Une ou plusieurs villes (autocomplétion France), avec rayon autour de chaque point.
          </p>

          {localisations.map((row, index) => (
            <div key={index} className="p-4 rounded-xl border border-gray-100 bg-gray-50/80 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <span className="text-xs font-semibold text-gray-500">Zone {index + 1}</span>
                {localisations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLocalisation(index)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Retirer
                  </button>
                )}
              </div>
              <LocationAutocomplete
                value={row.label}
                onChange={v => updateLoc(index, { label: v })}
                placeholder="Ville ou commune..."
              />
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                  <span>Rayon max.</span>
                  <span className="font-semibold text-[#2a52a8]">{row.distance_km} km</span>
                </div>
                <RangeSlider
                  min={5} max={150} step={5}
                  value={row.distance_km}
                  onChange={v => updateLoc(index, { distance_km: v })}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addLocalisation}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2a52a8] hover:underline"
          >
            <Plus size={16} />
            Ajouter une zone géographique
          </button>
        </div>

        {/* Télétravail & ESG */}
        <div className="border-t border-gray-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label id="label-teletravail" htmlFor="field-teletravail" className="block text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <Monitor size={15} className="text-[#2a52a8]" />
              Télétravail
            </label>
            <SelectDropdown
              id="field-teletravail"
              aria-labelledby="label-teletravail"
              value={teletravail}
              onChange={setTeletravail}
              options={TELETRAVAIL_OPTIONS}
            />
          </div>

          <div>
            <label id="label-esg" htmlFor="field-esg" className="block text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <Leaf size={15} className="text-[#2a52a8]" />
              Importance de l&apos;impact ESG chez l&apos;employeur
            </label>
            <SelectDropdown
              id="field-esg"
              aria-labelledby="label-esg"
              value={importanceEsg}
              onChange={setImportanceEsg}
              options={ESG_OPTIONS}
            />
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl mb-4">{error}</p>}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all shadow-sm hover:opacity-90 disabled:opacity-60"
        style={{ background: savedFlash ? '#16a34a' : '#2a52a8' }}
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        {savedFlash ? 'Enregistré !' : saving ? 'Enregistrement...' : 'Enregistrer'}
      </button>

      {/* ── Modal suppression compte ── */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(10,10,10,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8"
            onClick={e => e.stopPropagation()}
          >
            {/* Icône */}
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-5"
              style={{ background: '#fef2f2' }}>
              <Trash2 size={24} style={{ color: '#dc2626' }} />
            </div>

            {/* Titre */}
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Supprimer mon compte
            </h2>
            <p className="text-sm text-gray-500 text-center leading-relaxed mb-7">
              Cette action est <span className="font-semibold text-gray-700">irréversible</span>.
              Toutes vos données seront définitivement effacées — alertes, favoris et profil.
            </p>

            {/* Boutons */}
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: '#dc2626' }}
              >
                {deleting
                  ? <><Loader2 size={15} className="animate-spin" /> Suppression…</>
                  : 'Oui, supprimer définitivement'
                }
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="w-full py-3 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonProfil;
